/* eslint-disable react/no-array-index-key */
import AppleIcon from '@mui/icons-material/Apple';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import InfoIcon from '@mui/icons-material/Info';
import KeyIcon from '@mui/icons-material/Key';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LogoutIcon from '@mui/icons-material/Logout';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import PlaceIcon from '@mui/icons-material/Place';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import CloakFetcher from 'cloak-auth/src/Client/CloakFetcher';
import { logout } from 'cloak-auth/src/Client/logout';
import { useAuthStore } from 'cloak-auth/src/Client/stores';
import {
    useCreateUserGDPRRequest,
    useDeleteAccount,
    useGetCloakUser,
    useGetCloakUserFederatedIdentities,
    useGetInstances,
    useGetUserGDPRRequests,
    useGetUserSessions,
    useLeaveInstance,
    useLogoutAllSessions,
    useUpdateCloakUser,
} from 'gen/client';
import { InstanceDTO } from 'gen/model';
import { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import useSWR, { KeyedMutator } from 'swr';

import NavigationLink from '@ui/Components/Link/NavigationLink';

import AvatarImageWrapper from 'kerberus-components/src/Components/Avatars/AvatarImageWrapper';
import { useSSOFetchCloud } from 'kerberus-components/src/Components/Backend/SSOCloudFetcher';
import GetFormInput from 'kerberus-components/src/Components/Form/GetFormInput';
import { UploadType } from 'kerberus-components/src/Components/Image/UploadType';
import { SectionHeader } from 'kerberus-components/src/Components/Layout/SectionHeader';
import { InstanceDescription, User } from 'kerberus-components/src/Types/AuthServer';
import Capitalize from 'kerberus-components/src/Utils/Capitalize';
import {
    customErrorToast,
    customSuccessToast,
    settingsUpdatedError,
    settingsUpdatedSuccess,
} from 'kerberus-components/src/Utils/toasts';

import ViewContainer from '../../Components/Layout/ViewContainer';

// Types
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number | undefined;
}

interface EditUserFormInputs {
    email: string;
    firstName: string;
    lastName: string;
    userName: string;
}

interface DownloadFormInputs {
    format: 'json' | 'csv' | 'xml' | 'pdf';
}

interface InstanceSettingsFormInputs {
    allow_tracking: boolean;
    enable_days: number[];
    location_enabled: boolean;
    public: boolean;
    public_calendar: boolean;
    public_social: boolean;
    push_enabled: boolean;
    utc_end: string;
    utc_start: string;
}

interface InstanceCardProps {
    instance: InstanceDTO;
    isExpanded: boolean;
    isLoading: boolean;
    mutateInstances: KeyedMutator<InstanceDTO[]>;
    onExpandChange: (instanceId: string, isExpanded: boolean) => void;
}

function TabPanel({ children, index, value }: Readonly<TabPanelProps>) {
    return (
        <div
            aria-labelledby={`settings-tab-${index.toString()}`}
            hidden={value !== index}
            id={`settings-tabpanel-${index.toString()}`}
            role="tabpanel"
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

// Renamed component that includes both accordion and form
function InstanceCard({
    instance,
    isExpanded,
    isLoading,
    mutateInstances,
    onExpandChange,
}: Readonly<InstanceCardProps>) {
    const { fetchCloud } = useSSOFetchCloud();
    const { data: instanceDescription } = useSWR<InstanceDescription>(
        instance.domain ? ['tasks/instance_description', instance.id] : null,
        (route: string) => CloakFetcher(route, instance.domain),
    );

    const { trigger: leaveInstanceTrigger } = useLeaveInstance(instance.cloak_role);

    const { data: instanceProfile } = useSWR<User>(
        instance.domain ? ['users/timetableprofile/', instance.id] : null,
        (route: string) => CloakFetcher(route, instance.domain),
    );

    // State for the leave instance confirmation dialog
    const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
    const [isLeavingInstance, setIsLeavingInstance] = useState(false);

    const handleLeaveDialogOpen = () => {
        setIsLeaveDialogOpen(true);
    };

    const handleLeaveDialogClose = () => {
        setIsLeaveDialogOpen(false);
    };

    const handleLeaveInstance = async () => {
        setIsLeavingInstance(true);
        try {
            await leaveInstanceTrigger(instance.id);
            setIsLeavingInstance(false);
            setIsLeaveDialogOpen(false);

            // Refresh the instances list and close the current instance card
            await mutateInstances();
            onExpandChange(instance.id, false);
            customSuccessToast({
                message: 'Successfully left the instance',
                options: { id: 'leaveInstanceSuccess' },
            });
        } catch (error) {
            console.error(error);
            setIsLeavingInstance(false);
            customErrorToast({
                message: 'Failed to leave the instance',
                options: { id: 'leaveInstanceError' },
            });
        }
    };

    const { control, handleSubmit, reset } = useForm<InstanceSettingsFormInputs>({
        defaultValues: {
            allow_tracking: false,
            enable_days: [],
            location_enabled: false,
            public: false,
            public_calendar: false,
            public_social: false,
            push_enabled: false,
            utc_end: '',
            utc_start: '',
        },
    });

    useEffect(() => {
        //console.log(import.meta.env.DEV);
        if (instanceProfile) {
            reset({
                allow_tracking: instanceProfile.allow_tracking,
                enable_days: instanceProfile.enable_days,
                location_enabled: instanceProfile.location_enabled,
                public: instanceProfile.public,
                public_calendar: instanceProfile.public_calendar,
                public_social: instanceProfile.public_social,
                push_enabled: instanceProfile.push_enabled,
                utc_end: instanceProfile.utc_end || '',
                utc_start: instanceProfile.utc_start || '',
            });
        }
    }, [instanceProfile, reset]);

    const { dirtyFields } = useFormState({ control });

    const onSubmit = async (data: InstanceSettingsFormInputs) => {
        const dirtyData: Partial<InstanceSettingsFormInputs> = Object.fromEntries(
            Object.entries(data).filter(([key]) => dirtyFields[key as keyof InstanceSettingsFormInputs]),
        );

        try {
            await fetchCloud(`users/timetableprofile/update/`, 'patch', dirtyData, false, false, instance.domain);
            customSuccessToast(settingsUpdatedSuccess);
            reset(data);
        } catch (error) {
            customErrorToast(settingsUpdatedError);
            console.error(error);
        }
    };

    const handleAccordionChange = (_event: React.SyntheticEvent, expanded: boolean) => {
        onExpandChange(instance.id, expanded);
    };

    const type = instanceDescription?.instance_type ? Capitalize(instanceDescription.instance_type) : '';
    const address = instanceDescription?.address ? Capitalize(instanceDescription.address) : '';

    const instanceLocation = (
        <Stack
            component="span"
            direction="row"
            spacing={1}
            sx={{
                alignItems: 'center',
            }}
        >
            <Tooltip title="Online">
                <Box
                    component="span"
                    sx={{
                        backgroundColor: instanceDescription ? 'mlGreen.main' : 'mlRed.main',
                        borderRadius: '50%',
                        height: 13,
                        width: 13,
                    }}
                />
            </Tooltip>
            <Typography component="span">{`${type!}${type && address ? ', ' : ''} ${address!}`}</Typography>
        </Stack>
    );

    return (
        <Accordion elevation={2} expanded={isExpanded} onChange={handleAccordionChange}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    '& .MuiAccordionSummary-content': {
                        margin: 0,
                    },
                    pl: 0,
                }}
            >
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 2, width: '100%' }}>
                    <Box sx={{ aspectRatio: '16 / 9', height: 60 }}>
                        <AvatarImageWrapper
                            imagePath={instanceDescription?.image}
                            imageType={UploadType.Instance}
                            isLoading={false}
                            sx={{ height: '100%', width: '100%' }}
                            variant="square"
                        />
                    </Box>

                    <Box
                        sx={{
                            alignItems: 'flex-start',
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                lineHeight: 1.25,
                            }}
                            variant="subtitle1"
                        >
                            {instance.name}
                        </Typography>
                        <Typography
                            sx={{
                                color: 'text.secondary',
                            }}
                            variant="body2"
                        >
                            {instanceLocation}
                        </Typography>
                    </Box>
                    <Chip
                        color={instanceDescription?.public_operator ? 'info' : 'default'}
                        label={instanceDescription?.public_operator ? 'Public' : 'Private'}
                        size="small"
                        sx={{ display: { md: 'flex', xs: 'none' } }}
                    />
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                {/* URL is displayed at the top of the details with a clear button */}
                <Box
                    sx={{
                        alignItems: 'center',
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        display: 'flex',
                        gap: 1,
                        mb: 3,
                        p: 1.5,
                    }}
                >
                    <Typography
                        sx={{
                            color: 'text.secondary',
                        }}
                        variant="body2"
                    >
                        Instance URL:
                    </Typography>
                    <Typography sx={{ flexGrow: 1, wordBreak: 'break-all' }} variant="body2">
                        {instance.domain}
                    </Typography>
                    {instance.domain ? (
                        <Button
                            component="a"
                            href={instance.domain}
                            rel="noopener noreferrer"
                            size="small"
                            sx={{ minWidth: 100, ml: 'auto' }}
                            target="_blank"
                            variant="outlined"
                        >
                            Visit Site
                        </Button>
                    ) : null}
                </Box>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Stack spacing={2}>
                        <Typography variant="h6">Data Sharing Settings</Typography>
                        <Divider />
                        <GetFormInput
                            control={control}
                            helperText="Enable this option to allow other users see your current location within the site. At least one device with location enabled is required. If disabled, other users will see the time of your last activity on the site instead."
                            label="Share Location"
                            type="boolean"
                            value="location_enabled"
                        />
                        <GetFormInput
                            control={control}
                            helperText="Enable this option to activate push notifications, providing instant alerts for emoji messages or one-time tracking events. Please ensure notifications are enabled on your device to receive push notifications."
                            label="Push Notifications"
                            type="boolean"
                            value="push_enabled"
                        />
                        <GetFormInput
                            control={control}
                            helperText="Enable this option to become visible to other users in your company and social groups, allowing them to see your profile and connect with you. Note that you can adjust your visibility settings within each group."
                            label="Public Social"
                            type="boolean"
                            value="public_social"
                        />
                        <GetFormInput
                            control={control}
                            helperText="Enable this option to appear on the company and social group infoscreens, where your profile may be displayed alongside other users within your company or social group."
                            label="Public Infoscreen"
                            type="boolean"
                            value="public"
                        />
                        <GetFormInput
                            control={control}
                            helperText="Enable this option to grant other users access to your activity calendar, revealing when you've been active on the site and when you haven't."
                            label="Public Calendar"
                            type="boolean"
                            value="public_calendar"
                        />
                        <GetFormInput
                            control={control}
                            helperText="Enable this option to activate the One-time Tracking feature, allowing other users to receive notifications when you are active or inactive on the site."
                            label="Allow One-Time Tracking"
                            type="boolean"
                            value="allow_tracking"
                        />
                        <Divider />
                        <SectionHeader
                            description="Manage your profile's visibility by setting the time you're visible and the days you're active. 
                        Adjust these settings to control when other users can see your profile activity. 
                        These settings affect both the company and the groups. Note that groups also have a group-specific visibility time."
                            title="Schedule Your Visibility"
                        />

                        <Stack direction="row" spacing={2}>
                            <GetFormInput control={control} label="Start Time" type="date" value="utc_start" />
                            <GetFormInput control={control} label="End Time" type="date" value="utc_end" />
                        </Stack>
                        <GetFormInput control={control} label="Active Days" type="days" value="enable_days" />
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                justifyContent: 'flex-end',
                                mt: 2,
                            }}
                        >
                            <Button
                                color="error"
                                onClick={handleLeaveDialogOpen}
                                size="small"
                                startIcon={<DeleteIcon />}
                                variant="contained"
                            >
                                Leave instance
                            </Button>
                            <Button
                                disabled={isLoading}
                                startIcon={isLoading ? <CircularProgress size={20} /> : null}
                                type="submit"
                                variant="contained"
                            >
                                {isLoading ? 'Saving...' : 'Save Settings'}
                            </Button>
                        </Box>
                    </Stack>
                </form>
            </AccordionDetails>
            {/* Leave Instance Confirmation Dialog */}
            <Dialog fullWidth maxWidth="sm" onClose={handleLeaveDialogClose} open={isLeaveDialogOpen}>
                <DialogTitle>Leave Instance</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Alert severity="warning">
                            Warning: This action will remove your account from this instance. You will lose access to
                            all data associated with your profile in this instance. This action cannot be undone.
                        </Alert>
                        <Typography variant="body1">Are you sure you want to leave {instance.name}?</Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleLeaveDialogClose}>Cancel</Button>
                    <Button
                        color="error"
                        disabled={isLeavingInstance}
                        onClick={handleLeaveInstance}
                        startIcon={isLeavingInstance ? <CircularProgress size={20} /> : null}
                        variant="contained"
                    >
                        {isLeavingInstance ? 'Leaving...' : 'Leave Instance'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Accordion>
    );
}

export default function Settings() {
    const theme = useTheme();
    const { data: instances = [], isLoading: isLoadingInstances, mutate: mutateInstances } = useGetInstances();
    const location = useLocation();
    const navigate = useNavigate();

    const [tabValue, setTabValue] = useState(() => {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        const tabMap: Record<string, number> = {
            data: 3,
            instances: 1,
            profile: 0,
            sessions: 2,
        };
        return tabParam && tabParam in tabMap ? tabMap[tabParam] : 0;
    });

    const {
        data: sessions,
        error: sessionsError,
        isLoading: sessionsIsLoading,
    } = useGetUserSessions({
        swr: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        },
    });

    const { data: gdprRequests, error: gdprRequestError, isLoading: gdprRequestLoading } = useGetUserGDPRRequests();

    const { data: cloakUserData, error: cloakUserError, isLoading: cloakUserLoading } = useGetCloakUser();

    const { data: cloakFederationData } = useGetCloakUserFederatedIdentities();

    const { trigger: gdprRequestTrigger } = useCreateUserGDPRRequest();

    const { trigger: sessionLogoutTrigger } = useLogoutAllSessions({});

    const { trigger: deleteUserTrigger } = useDeleteAccount({});

    const { trigger: updateUserTrigger } = useUpdateCloakUser({});

    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [isGdprDialogOpen, setIsGdprDialogOpen] = useState(false);
    const [isRequestingGdpr, setIsRequestingGdpr] = useState(false);

    const [currentAction, setCurrentAction] = useState<'all' | 'tab'>('all');
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
    const [downloadExpiry, setDownloadExpiry] = useState<string | null>(null);
    const [deleteStatus, setDeleteStatus] = useState<{ message: string; status: string } | null>(null);
    const [expandedInstance, setExpandedInstance] = useState<string | false>(() => {
        const params = new URLSearchParams(location.search);
        const instanceId = params.get('id');
        return instanceId || false;
    });

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const instanceId = params.get('id');

        if (instanceId) {
            setTabValue(1);
            setExpandedInstance(instanceId);
        }
    }, [location.search]);

    const {
        control: editControl,
        handleSubmit: handleEditSubmit,
        setValue: editSetValue,
    } = useForm<EditUserFormInputs>({
        defaultValues: {
            email: cloakUserData?.email,
            firstName: cloakUserData?.firstName,
            lastName: cloakUserData?.lastName,
            userName: cloakUserData?.username,
        },
    });

    const { control: downloadControl, handleSubmit: handleDownloadSubmit } = useForm<DownloadFormInputs>({
        defaultValues: {
            format: 'json',
        },
    });

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);

        const tabNames = ['profile', 'instances', 'sessions', 'data'];

        const params = new URLSearchParams(location.search);
        params.set('tab', tabNames[newValue]!);

        params.delete('id');
        setExpandedInstance(false);

        navigate(`/settings?${params.toString()}`, { replace: true });
    };

    const handleEditDialogOpen = () => {
        editSetValue('firstName', cloakUserData?.firstName || '');
        editSetValue('lastName', cloakUserData?.lastName || '');
        editSetValue('userName', cloakUserData?.username || '');
        editSetValue('email', cloakUserData?.email || '');
        setIsEditDialogOpen(true);
    };

    const handleEditDialogClose = () => {
        setIsEditDialogOpen(false);
    };

    const handleDownloadDialogClose = () => {
        setIsDownloadDialogOpen(false);
        setDownloadUrl(null);
        setDownloadExpiry(null);
    };

    const handleDeleteDialogClose = () => {
        setIsDeleteDialogOpen(false);
        setDeleteStatus(null);
    };

    const handleLogoutDialogOpen = () => {
        setIsLogoutDialogOpen(true);
    };

    const handleLogoutDialogClose = () => {
        setIsLogoutDialogOpen(false);
    };

    const handleLogoutAllSessions = async () => {
        setIsLoggingOut(true);
        try {
            await sessionLogoutTrigger();
            setIsLoggingOut(false);
            setIsLogoutDialogOpen(false);

            customSuccessToast({
                message: 'Successfully logged out all sessions',
                options: { id: 'logoutAllSessionsSuccess' },
            });

            // Logout the current session as well
            void logout(useAuthStore.getState().keyCloak);
        } catch (error) {
            console.error(error);
            setIsLoggingOut(false);
            customErrorToast({
                message: 'Failed to logout all sessions',
                options: { id: 'logoutAllSessionsError' },
            });
        }
    };

    const handleGdprDialogOpen = () => {
        setIsGdprDialogOpen(true);
    };

    const handleGdprDialogClose = () => {
        setIsGdprDialogOpen(false);
    };

    const handleGdprRequest = async () => {
        setIsRequestingGdpr(true);
        try {
            await gdprRequestTrigger();
            setIsRequestingGdpr(false);
            setIsGdprDialogOpen(false);

            customSuccessToast({
                message: 'Your GDPR data request has been submitted successfully',
                options: { id: 'gdprRequestSuccess' },
            });
        } catch (error) {
            console.error(error);
            setIsRequestingGdpr(false);
            customErrorToast({
                message: 'Failed to submit GDPR data request',
                options: { id: 'gdprRequestError' },
            });
        }
    };

    const handleDeleteDialogOpen = (type: 'all' | 'tab') => {
        setCurrentAction(type);
        setIsDeleteDialogOpen(true);
    };

    const handleInstanceExpandChange = (instanceId: string, isExpanded: boolean) => {
        setExpandedInstance(isExpanded ? instanceId : false);

        if (isExpanded) {
            const params = new URLSearchParams(location.search);
            params.set('id', instanceId);
            navigate(`/settings?${params.toString()}`, { replace: true });
        } else {
            const params = new URLSearchParams(location.search);
            params.delete('id');
            navigate(`/settings?${params.toString()}`, { replace: true });
        }
    };

    const onEditSubmit = async (data: EditUserFormInputs) => {
        setIsLoading(true);

        Object.values(data).forEach((value) => {
            if (value === undefined || value === null || value === '') {
                customErrorToast(settingsUpdatedError);
                setIsLoading(false);
                return;
            }
        });

        try {
            await updateUserTrigger({
                email: data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                username: data.userName,
            });
        } catch (error) {
            customErrorToast(settingsUpdatedError);
            console.error(error);
            return;
        } finally {
            setIsLoading(false);
            setIsEditDialogOpen(false);
        }
    };

    const dataTypes = ['info', 'instances', 'sessions', 'data'];
    const dataType: string = currentAction === 'all' ? 'all' : dataTypes[tabValue || 0] || 'unknown';

    const onDownloadSubmit = (data: DownloadFormInputs) => {
        setIsLoading(true);

        // API call
        setTimeout(() => {
            setDownloadUrl(`https://example.com/downloads/settings_${dataType}_data.${data.format}`);
            const expiryDate = new Date();
            expiryDate.setHours(expiryDate.getHours() + 24);
            setDownloadExpiry(expiryDate.toISOString());
            setIsLoading(false);
        }, 1000);
    };

    const onDeleteRequest = async () => {
        setIsLoading(true);
        try {
            await deleteUserTrigger({});
        } catch {
            setIsLoading(false);
            setDeleteStatus({
                message: `An error occured when trying to delete your account. Please try again later, or contact support.`,
                status: 'error',
            });

            return;
        }

        setIsLoading(false);

        setDeleteStatus({
            message: `Your request to delete your account was received and it will be processed soon, you will be logged out in a moment.`,
            status: 'success',
        });
        // user1 id 6a1812ae-66a2-4ca5-ba49-811a4d682ead
        // API delete call
        setTimeout(() => {
            void logout(useAuthStore.getState().keyCloak);
        }, 2000);
    };

    const getTabLabel = () => {
        switch (tabValue) {
            case 0:
                return 'Personal Information';
            case 1:
                return 'Instances Settings';
            case 2:
                return 'Active Sessions';
            case 3:
                return 'Data requests';
            default:
                return 'Settings';
        }
    };

    return (
        <ViewContainer px={{ sm: 2, xs: 1 }}>
            <Stack spacing={{ lg: 3, xs: 1 }}>
                <Box
                    sx={{
                        alignItems: { md: 'center', xs: 'flex-start' },
                        display: 'flex',
                        flexDirection: { md: 'row', xs: 'column' },
                        gap: 2,
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography
                        component="h1"
                        sx={{
                            // Hide title on mobile since it's shown in the AppBar
                            display: { lg: 'flex', xs: 'none' },
                        }}
                        variant="h4"
                    >
                        Settings
                    </Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                    This settings page provides access to all your account information and preferences. You can manage
                    your personal details, security settings, and instance configurations.
                </Alert>

                <Paper elevation={2}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            aria-label="settings tabs"
                            onChange={handleTabChange}
                            scrollButtons="auto"
                            value={tabValue}
                            variant={window.innerWidth < 600 ? 'scrollable' : 'standard'}
                        >
                            <Tab icon={<InfoIcon />} iconPosition="start" id="settings-tab-0" label="Profile" />
                            <Tab icon={<PlaceIcon />} iconPosition="start" id="settings-tab-1" label="Instances" />
                            <Tab icon={<KeyIcon />} iconPosition="start" id="settings-tab-2" label="Sessions" />
                            <Tab icon={<FolderSharedIcon />} iconPosition="start" id="settings-tab-3" label="Data" />
                        </Tabs>
                    </Box>

                    {/* Header for current tab with actions */}
                    <Box
                        sx={{
                            alignItems: { sm: 'center', xs: 'flex-start' },
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            flexDirection: { sm: 'row', xs: 'column' },
                            gap: 1,
                            justifyContent: 'space-between',
                            p: 2,
                        }}
                    >
                        <Typography variant="h6">{getTabLabel()}</Typography>
                        {tabValue === 2 && (
                            <Button
                                color="error"
                                onClick={handleLogoutDialogOpen}
                                size="small"
                                startIcon={<LogoutIcon />}
                                variant="outlined"
                            >
                                Logout All Sessions
                            </Button>
                        )}
                        {tabValue === 0 && (
                            <Button
                                color="error"
                                onClick={() => handleDeleteDialogOpen('all')}
                                startIcon={<DeleteIcon />}
                                sx={{ minWidth: { sm: 'auto', xs: '100%' } }}
                                variant="outlined"
                            >
                                Delete account & data
                            </Button>
                        )}
                    </Box>

                    {/* Personal Info Tab */}
                    <TabPanel index={0} value={tabValue}>
                        <Typography
                            sx={{
                                color: 'text.secondary',
                                mb: 3,
                            }}
                            variant="body2"
                        >
                            View and manage your personal information, account details, and connected social accounts.
                        </Typography>
                        {cloakUserLoading ? (
                            <Box>
                                <Typography>Loading...</Typography>
                            </Box>
                        ) : cloakUserError ? (
                            <div>error fetching data</div>
                        ) : (
                            <Box sx={{ alignItems: 'flex-start', display: 'flex', justifyContent: 'space-between' }}>
                                <Stack spacing={2} sx={{ width: '100%' }}>
                                    <Box sx={{ display: 'flex', flexDirection: { sm: 'row', xs: 'column' }, gap: 4 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                                variant="subtitle2"
                                            >
                                                User ID
                                            </Typography>
                                            <Typography variant="body1">{cloakUserData?.userId}</Typography>
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                                variant="subtitle2"
                                            >
                                                Name
                                            </Typography>
                                            <Typography variant="body1">
                                                {cloakUserData?.firstName} {cloakUserData?.lastName}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                                variant="subtitle2"
                                            >
                                                Email
                                            </Typography>
                                            <Typography variant="body1">{cloakUserData?.email}</Typography>
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                                variant="subtitle2"
                                            >
                                                Account Created
                                            </Typography>
                                            <Typography variant="body1">
                                                {new Date(cloakUserData?.createdAt ?? '').toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Federated Identities Section */}
                                    <Box sx={{ mt: 2 }}>
                                        <Typography
                                            sx={{
                                                color: 'text.secondary',
                                                mb: 1,
                                            }}
                                            variant="subtitle2"
                                        >
                                            Connected Accounts
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            {Array.isArray(cloakFederationData) && cloakFederationData.length === 0 ? (
                                                <Typography
                                                    sx={{
                                                        color: 'text.secondary',
                                                    }}
                                                    variant="body2"
                                                >
                                                    No connected accounts
                                                </Typography>
                                            ) : (
                                                cloakFederationData?.map((item, idx) => {
                                                    let icon: React.ReactElement | undefined = undefined;
                                                    let label = '';
                                                    switch (item.identityProvider) {
                                                        case 'apple':
                                                            icon = <AppleIcon />;
                                                            label = 'Apple';
                                                            break;
                                                        case 'microsoft':
                                                            icon = <MicrosoftIcon />;
                                                            label = 'Microsoft';
                                                            break;
                                                        case 'linkedin-openid-connect':
                                                            icon = <LinkedInIcon />;
                                                            label = 'LinkedIn';
                                                            break;
                                                        default:
                                                            label = item.identityProvider;
                                                    }
                                                    return (
                                                        <Chip
                                                            color="primary"
                                                            icon={icon}
                                                            key={`${item.identityProvider}-${String(idx)}`}
                                                            label={label}
                                                            sx={{ fontWeight: 500 }}
                                                            variant="outlined"
                                                        />
                                                    );
                                                })
                                            )}
                                            <NavigationLink
                                                to={
                                                    (import.meta.env.EXPO_PUBLIC_CLOAK_HOST as string) +
                                                    '/realms/renki/account/identity'
                                                }
                                            >
                                                <Button size="small" variant="outlined">
                                                    Manage Connected Accounts
                                                </Button>
                                            </NavigationLink>
                                        </Stack>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                onClick={() =>
                                                    useAuthStore
                                                        .getState()
                                                        .keyCloak?.login({ action: 'UPDATE_PASSWORD' })
                                                }
                                                startIcon={<EditIcon />}
                                                variant="contained"
                                            >
                                                Reset Password
                                            </Button>
                                            <Button
                                                onClick={handleEditDialogOpen}
                                                startIcon={<EditIcon />}
                                                variant="contained"
                                            >
                                                Edit Profile
                                            </Button>
                                        </Box>
                                    </Box>
                                </Stack>
                            </Box>
                        )}
                    </TabPanel>

                    {/* Instances Tab */}
                    <TabPanel index={1} value={tabValue}>
                        <Typography
                            sx={{
                                color: 'text.secondary',
                                mb: 3,
                            }}
                            variant="body2"
                        >
                            Configure your instance settings, manage data sharing preferences, and visibility schedules
                            for each registered instance.
                        </Typography>
                        {isLoadingInstances ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : instances.length === 0 ? (
                            <Alert severity="info">
                                No instances found. Instances will appear here once they are registered.
                            </Alert>
                        ) : (
                            <Stack spacing={3}>
                                {instances.map((instance) => (
                                    <InstanceCard
                                        instance={instance}
                                        isExpanded={expandedInstance === instance.id}
                                        isLoading={isLoading}
                                        key={instance.id}
                                        mutateInstances={mutateInstances}
                                        onExpandChange={handleInstanceExpandChange}
                                    />
                                ))}
                            </Stack>
                        )}
                    </TabPanel>

                    {/* Sessions Tab */}
                    <TabPanel index={2} value={tabValue}>
                        <Typography
                            sx={{
                                color: 'text.secondary',
                                mb: 3,
                            }}
                            variant="body2"
                        >
                            Monitor your active login sessions across devices and manage session security by logging out
                            from all sessions.
                        </Typography>
                        {sessionsIsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : sessionsError !== undefined ? (
                            <Alert severity="info">
                                No active sessions found. Your current session will appear here once you log in.
                            </Alert>
                        ) : (
                            sessions?.sessions.map((session, index) => (
                                <Paper
                                    elevation={1}
                                    key={index}
                                    sx={{
                                        alignItems: { sm: 'center', xs: 'flex-start' },
                                        display: 'flex',
                                        flexDirection: { sm: 'row', xs: 'column' },
                                        gap: 2,
                                        justifyContent: 'space-between',
                                        mb: 2,
                                        p: 2,
                                    }}
                                >
                                    <Box>
                                        <Typography variant="subtitle1">IP: {session.ip}</Typography>
                                        <Typography
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                            variant="body2"
                                        >
                                            Session IDs: {session.clients}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2">
                                            Start: {new Date(session.started).toLocaleString()}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2">
                                            Last active: {new Date(session.lastAccess).toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Paper>
                            ))
                        )}
                    </TabPanel>

                    {/* GDPR Tab */}
                    <TabPanel index={3} value={tabValue}>
                        <Typography
                            sx={{
                                color: 'text.secondary',
                                mb: 3,
                            }}
                            variant="body2"
                        >
                            Submit GDPR data requests to receive a copy of all your personal data stored in our systems
                            according to your privacy rights.
                        </Typography>
                        {gdprRequestLoading ? (
                            <Box>
                                <Typography>Loading...</Typography>
                            </Box>
                        ) : gdprRequestError !== undefined ? (
                            <div>error fetching requests</div>
                        ) : (
                            <>
                                {gdprRequests?.map((request) => (
                                    <Paper
                                        elevation={1}
                                        key={request.gdprRequestId}
                                        sx={{
                                            alignItems: { sm: 'center', xs: 'flex-start' },
                                            display: 'flex',
                                            flexDirection: { sm: 'row', xs: 'column' },
                                            gap: 2,
                                            justifyContent: 'space-between',
                                            mb: 2,
                                            p: 2,
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="subtitle1">Completed</Typography>
                                            <Typography
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                                variant="body2"
                                            >
                                                {request.completedAt === null ? 'no' : 'yes'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle1">Email</Typography>
                                            <Typography
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                                variant="body2"
                                            >
                                                {request.userEmail}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2">
                                            Created: {new Date(request.createdAt).toLocaleString()}
                                        </Typography>
                                    </Paper>
                                ))}
                                {gdprRequests?.some((e) => e.completedAt === null) ? (
                                    <div>You have an active request, you cannot make a new one</div>
                                ) : (
                                    <Button
                                        onClick={handleGdprDialogOpen}
                                        startIcon={<DownloadIcon />}
                                        variant="contained"
                                    >
                                        Make a data request
                                    </Button>
                                )}
                            </>
                        )}
                    </TabPanel>
                </Paper>
            </Stack>
            {/* Edit Dialog */}
            <Dialog fullWidth maxWidth="sm" onClose={handleEditDialogClose} open={isEditDialogOpen}>
                <DialogTitle>Edit Profile</DialogTitle>
                <form onSubmit={handleEditSubmit(onEditSubmit)}>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 1 }}>
                            <GetFormInput
                                control={editControl}
                                helperText="Your first name as it will appear in the system"
                                label="First Name"
                                required
                                type="string"
                                value="firstName"
                            />
                            <GetFormInput
                                control={editControl}
                                helperText="Your last name as it will appear in the system"
                                label="Last Name"
                                required
                                type="string"
                                value="lastName"
                            />
                            <GetFormInput
                                control={editControl}
                                helperText="Your primary email address for notifications"
                                label="Email Address"
                                required
                                type="string"
                                value="email"
                            />
                            <GetFormInput
                                control={editControl}
                                helperText="Your Username"
                                label="Username"
                                required
                                type="string"
                                value="userName"
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleEditDialogClose}>Cancel</Button>
                        <Button
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={20} /> : null}
                            type="submit"
                            variant="contained"
                        >
                            {isLoading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
            {/* Download Dialog */}
            <Dialog fullWidth maxWidth="sm" onClose={handleDownloadDialogClose} open={isDownloadDialogOpen}>
                <DialogTitle>Download {currentAction === 'all' ? 'All' : getTabLabel()} Data</DialogTitle>
                <form onSubmit={handleDownloadSubmit(onDownloadSubmit)}>
                    <DialogContent>
                        {!downloadUrl ? (
                            <Stack spacing={3} sx={{ mt: 1 }}>
                                <Alert severity="info">
                                    You can download{' '}
                                    {currentAction === 'all'
                                        ? 'all your data'
                                        : `your ${getTabLabel().toLowerCase()} data`}{' '}
                                    in various formats. The download link will expire after 24 hours.
                                </Alert>
                                <GetFormInput
                                    control={downloadControl}
                                    helperText="Choose the format for your data export"
                                    label="File Format"
                                    options={[
                                        { label: 'JSON', value: 'json' },
                                        { label: 'CSV', value: 'csv' },
                                        { label: 'XML', value: 'xml' },
                                        { label: 'PDF', value: 'pdf' },
                                    ]}
                                    required
                                    type="selectOne"
                                    value="format"
                                />
                            </Stack>
                        ) : (
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                <Alert severity="success">
                                    Your download is ready! The link will expire in 24 hours.
                                </Alert>
                                <Box sx={{ bgcolor: 'background.default', borderRadius: 1, p: 2 }}>
                                    <Typography variant="body2">Download URL:</Typography>
                                    <Typography
                                        component="a"
                                        href={downloadUrl}
                                        rel="noopener noreferrer"
                                        sx={{ wordBreak: 'break-all' }}
                                        target="_blank"
                                        variant="body1"
                                    >
                                        {downloadUrl}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        color: 'text.secondary',
                                    }}
                                    variant="body2"
                                >
                                    Expires: {downloadExpiry ? new Date(downloadExpiry).toLocaleString() : 'N/A'}
                                </Typography>
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDownloadDialogClose}>{downloadUrl ? 'Close' : 'Cancel'}</Button>
                        {!downloadUrl && (
                            <Button
                                disabled={isLoading}
                                startIcon={isLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
                                type="submit"
                                variant="contained"
                            >
                                {isLoading ? 'Processing...' : 'Generate Download'}
                            </Button>
                        )}
                    </DialogActions>
                </form>
            </Dialog>
            {/* Delete Dialog */}
            <Dialog fullWidth maxWidth="sm" onClose={handleDeleteDialogClose} open={isDeleteDialogOpen}>
                <DialogTitle>Delete {currentAction === 'all' ? '' : getTabLabel()} Account and Data</DialogTitle>
                <DialogContent>
                    {!deleteStatus ? (
                        <Stack spacing={3} sx={{ mt: 1 }}>
                            <Alert severity="warning">
                                Warning: This action will initiate a request to delete
                                {currentAction === 'all'
                                    ? ' all your account and all data '
                                    : ` your ${getTabLabel().toLowerCase()} data `}
                                from our systems. This cannot be undone.
                            </Alert>
                            <Typography variant="body1">Are you sure you want to proceed with this request?</Typography>
                        </Stack>
                    ) : (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            {deleteStatus.message}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose}>{deleteStatus ? 'Close' : 'Cancel'}</Button>
                    {!deleteStatus && (
                        <Button
                            color="error"
                            disabled={isLoading}
                            onClick={onDeleteRequest}
                            startIcon={isLoading ? <CircularProgress size={20} /> : null}
                            variant="contained"
                        >
                            {isLoading ? 'Processing...' : 'Confirm Deletion Request'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            {/* Logout All Sessions Dialog */}
            <Dialog fullWidth maxWidth="sm" onClose={handleLogoutDialogClose} open={isLogoutDialogOpen}>
                <DialogTitle>Logout All Sessions</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Alert severity="warning">
                            Warning: This action will log out all active sessions across all your devices. You will need
                            to log in again on each device that was previously logged in.
                        </Alert>
                        <Typography variant="body1">Are you sure you want to logout from all sessions?</Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleLogoutDialogClose}>Cancel</Button>
                    <Button
                        color="error"
                        disabled={isLoggingOut}
                        onClick={handleLogoutAllSessions}
                        startIcon={isLoggingOut ? <CircularProgress size={20} /> : <LogoutIcon />}
                        variant="contained"
                    >
                        {isLoggingOut ? 'Logging out...' : 'Logout All Sessions'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* GDPR Data Request Dialog */}
            <Dialog fullWidth maxWidth="sm" onClose={handleGdprDialogClose} open={isGdprDialogOpen}>
                <DialogTitle>GDPR Data Request</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Alert severity="info">
                            According to GDPR regulations, you have the right to request a copy of all your personal
                            data. Once submitted, your request will be processed within the legally allowed time frame
                            of up to 30 days.
                        </Alert>
                        <Typography variant="body1">
                            When your data is ready, it will be sent to your registered email address:{' '}
                            <strong>{cloakUserData?.email}</strong>
                        </Typography>
                        <Typography variant="body1">Would you like to proceed with this GDPR data request?</Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleGdprDialogClose}>Cancel</Button>
                    <Button
                        disabled={isRequestingGdpr}
                        onClick={handleGdprRequest}
                        startIcon={isRequestingGdpr ? <CircularProgress size={20} /> : <DownloadIcon />}
                        variant="contained"
                    >
                        {isRequestingGdpr ? 'Submitting request...' : 'Submit Request'}
                    </Button>
                </DialogActions>
            </Dialog>
        </ViewContainer>
    );
}
